import { SINGLE_COUNTRY } from '@/config/singleCountry'

export function isNeedLogin(res) {
  return responseLayers(res).some(source => {
    const fields = [
      source.status,
      source.state,
      source.result,
      source.code,
      source.errorCode,
      source.success,
      source.marker,
      source.type,
      source.msg,
      source.message,
      source.errorMsg,
      source.errorMessage,
      source.reason,
      source.desc,
      source.description,
      primitiveErrorMessage(source.error)
    ]
    return source.needLogin === true ||
      source.need_login === true ||
      fields.some(isNeedLoginToken)
  })
}

export function isOkResponse(res) {
  return !!(res && hasSuccessMarker(res) && !hasUnavailableMarker(res) && !isNeedLogin(res))
}

export function safeString(value) {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

const ENVELOPE_KEYS = [
  'body',
  'content',
  'payload',
  'extra',
  'page',
  'data',
  'records',
  'dataList',
  'diagnostics',
  'diagnostic',
  'meta',
  'detail',
  'details',
  'error',
  'errors',
  'cause',
  'response'
]
const MAX_ENVELOPE_DEPTH = 8
const RESPONSE_BODY_KEYS = [
  'accessToken',
  'token',
  'access_token',
  'list',
  'platformList',
  'redirectUri',
  'url',
  'authUrl',
  'isReport',
  'currency',
  'fbPixId',
  'pixelId',
  'fbid',
  'target'
]

function safeGetStorageItem(storage, key, fallback = '') {
  if (!storage || typeof storage.getItem !== 'function') return fallback
  try {
    const value = storage.getItem(key)
    return value == null ? fallback : value
  } catch (e) {
    return fallback
  }
}

function browserWindow() {
  try {
    if (typeof window !== 'undefined' && window) return window
  } catch (e) {
    return null
  }
  return null
}

function safeLocalStorage() {
  try {
    const win = browserWindow()
    const storage = win && win.localStorage
    return storage && typeof storage.getItem === 'function' ? storage : null
  } catch (e) {
    return null
  }
}

export function contentOf(res) {
  if (!res || typeof res !== 'object') return {}
  return unwrapEnvelopeBody(firstEnvelopeBody(res))
}

function responseLayers(res) {
  return collectResponseLayers(res)
}

function normalizeEnvelopeBody(value) {
  if (Array.isArray(value)) return { list: value, dataList: value }
  return value && typeof value === 'object' ? value : {}
}

function firstEnvelopeBody(source) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object') return null
  const candidates = []
  for (const key of ENVELOPE_KEYS) {
    const value = source[key]
    if (Array.isArray(value)) return value
    if (value && typeof value === 'object') candidates.push(value)
  }
  const populated = candidates.find(value => {
    const body = unwrapEnvelopeBody(value)
    return RESPONSE_BODY_KEYS.some(key => Object.prototype.hasOwnProperty.call(body, key))
  })
  if (populated) return populated
  if (candidates.length) return candidates[0]
  if (RESPONSE_BODY_KEYS.some(key => Object.prototype.hasOwnProperty.call(source, key))) return source
  return null
}

function unwrapEnvelopeBody(value, depth = 0) {
  const body = normalizeEnvelopeBody(value)
  if (!Object.keys(body).length || depth >= MAX_ENVELOPE_DEPTH) return body
  for (const key of ENVELOPE_KEYS) {
    const nested = body[key]
    if (!nested || (typeof nested !== 'object' && !Array.isArray(nested))) continue
    const unwrapped = unwrapEnvelopeBody(nested, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    delete parent.body
    delete parent.content
    delete parent.payload
    delete parent.extra
    delete parent.page
    delete parent.data
    return {
      ...parent,
      ...unwrapped
    }
  }
  return body
}

function collectResponseLayers(source, depth = 0, out = [], seen = new Set()) {
  if (!source || typeof source !== 'object' || depth >= MAX_ENVELOPE_DEPTH || seen.has(source)) return out
  seen.add(source)
  if (Array.isArray(source)) {
    source.forEach(item => collectResponseLayers(item, depth + 1, out, seen))
    return out
  }
  if (!Array.isArray(source) && out.indexOf(source) === -1) out.push(source)
  for (const key of ENVELOPE_KEYS) {
    const value = source[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) continue
    const body = normalizeEnvelopeBody(value)
    if (!Object.keys(body).length || out.indexOf(body) !== -1) continue
    out.push(body)
    collectResponseLayers(value, depth + 1, out, seen)
  }
  return out
}

export function failMessage(res, fallback) {
  const message = firstMessage(res)
  if (isNeedLogin(res)) {
    if (message && isUserFacingNeedLoginMessage(message) && !isGenericFailureMessage(message, fallback)) return safeString(message)
    return 'Please log in to continue.'
  }
  if (hasUnavailableMarker(res)) {
    if (message && !isOptimisticSuccessMessage(message) && !isGenericFailureMessage(message, fallback) && !isTechnicalFailureMessage(message)) return safeString(message)
    return authUnavailableMessage(res, fallback)
  }
  if (message && !isGenericFailureMessage(message, fallback) && !isTechnicalFailureMessage(message)) return safeString(message)
  return defaultAuthFailureMessage(fallback)
}

function firstMessage(res) {
  return responseLayers(res)
    .map(source => source.msg || source.message || source.errorMsg || source.errorMessage || source.reason || source.desc || source.description || primitiveErrorMessage(source.error))
    .find(value => safeString(value))
}

function primitiveErrorMessage(value) {
  return value && typeof value === 'object' ? '' : value
}

function isNeedLoginToken(value) {
  const token = unavailableToken(value)
  if (!token) return false
  if ([
    'need_login',
    'needlogin',
    'login_required',
    'login required',
    'unauthorized',
    'not_authorized',
    'not authorized',
    'token_expired',
    'expired_token',
    'login_expired',
    'session_expired',
    '401'
  ].includes(token)) return true
  return token.includes('need_login') ||
    token.includes('needlogin') ||
    token.includes('login_required') ||
    token.includes('login required') ||
    token.includes('unauthorized') ||
    token.includes('not_authorized') ||
    token.includes('not authorized') ||
    token.includes('token_expired') ||
    token.includes('expired_token') ||
    token.includes('login_expired') ||
    token.includes('session_expired') ||
    /\b401\b/.test(token)
}

function isUserFacingNeedLoginMessage(value) {
  const message = safeString(value)
  const token = message.toLowerCase()
  if (!message || isTechnicalFailureMessage(message)) return false
  if ([
    'need_login',
    'needlogin',
    'login_required',
    'login required',
    'unauthorized',
    'not_authorized',
    'not authorized',
    'token_expired',
    'expired_token',
    'login_expired',
    'session_expired',
    '401'
  ].includes(token)) return false
  return token.includes('please log in') ||
    token.includes('please login') ||
    token.includes('log in again') ||
    token.includes('login again') ||
    token.includes('session expired') ||
    token.includes('token expired') ||
    token.includes('登录') ||
    token.includes('登入') ||
    token.includes('会话') ||
    token.includes('會話') ||
    token.includes('过期') ||
    token.includes('過期')
}

function defaultAuthFailureMessage(fallback) {
  const message = safeString(fallback)
  if (message && !isGenericFailureMessage(message) && !isTechnicalFailureMessage(message)) return message
  return 'La demande de connexion a échoué, veuillez réessayer plus tard.'
}

function authUnavailableMessage(res, fallback) {
  if (isNeedLogin(res)) return 'Veuillez vous connecter avant de continuer.'
  const layers = responseLayers(res)
  if (hasAnyActiveFlag(layers, ['technicalError', 'technical_error', 'technicalException', 'technical_exception', 'technicalMarker', 'technical_marker', 'backendFailure', 'backend_failure']) || hasAnyMarkerToken(layers, value => {
    const token = unavailableToken(value)
    return token.includes('technical_error') ||
      token.includes('technicalerror') ||
      token.includes('technical error') ||
      token.includes('technical-error') ||
      token.includes('technical_exception') ||
      token.includes('technicalexception') ||
      token.includes('technical exception') ||
      token.includes('technical-exception') ||
      token.includes('technical_marker') ||
      token.includes('technicalmarker') ||
      token.includes('technical marker') ||
      token.includes('technical-marker') ||
      token.includes('backend_failure') ||
      token.includes('backendfailure') ||
      token.includes('backend failure') ||
      token.includes('backend-failure') ||
      token.includes('技术错误') ||
      token.includes('技术异常')
  })) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (hasAnyActiveFlag(layers, ['readFailure', 'read_failure', 'readUnavailable', 'read_unavailable', 'readBlocked', 'read_blocked', 'readFailed', 'read_failed']) || hasAnyMarkerToken(layers, value => {
    const token = unavailableToken(value)
    return token.includes('read_failure') ||
      token.includes('readfailure') ||
      token.includes('read failure') ||
      token.includes('read-failure') ||
      token.includes('read_unavailable') ||
      token.includes('readunavailable') ||
      token.includes('read unavailable') ||
      token.includes('read-unavailable') ||
      token.includes('read_blocked') ||
      token.includes('readblocked') ||
      token.includes('read blocked') ||
      token.includes('read-blocked') ||
      token.includes('read_failed') ||
      token.includes('readfailed') ||
      token.includes('read failed') ||
      token.includes('read-failed')
  })) {
    return 'La lecture des données de connexion a échoué, veuillez réessayer plus tard.'
  }
  if (hasAnyActiveFlag(layers, ['dataUnavailable', 'data_unavailable'])) {
    return 'Le service de connexion est temporairement indisponible, veuillez réessayer plus tard.'
  }
  if (hasAnyActiveFlag(layers, ['blocked', 'backendBlocked', 'backend_blocked', 'writeBlocked', 'write_blocked', 'readOnlyAction', 'read_only_action', 'readOnly', 'read_only', 'readonly'])) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (hasAnyActiveFlag(layers, ['mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'fallback', 'fallbackUsed', 'fallback_used', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported'])) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (hasAnyMarkerToken(layers, value => {
    const token = unavailableToken(value)
    return token.includes('data_unavailable') ||
      token.includes('dataunavailable') ||
      token.includes('data unavailable') ||
      token.includes('data-unavailable') ||
      token.includes('fallback') ||
      token.includes('unavailable') ||
      token.includes('not_supported') ||
      token.includes('not supported') ||
      token.includes('unsupported')
  })) {
    return 'Le service de connexion est temporairement indisponible, veuillez réessayer plus tard.'
  }
  if (hasAnyMarkerToken(layers, value => {
    const token = unavailableToken(value)
    return token.includes('blocked') ||
      token.includes('read_only') ||
      token.includes('read only') ||
      token.includes('read-only') ||
      token.includes('mock') ||
      token.includes('stub') ||
      token.includes('unimplemented') ||
      token.includes('not_implemented') ||
      token.includes('not implemented')
  })) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  return defaultAuthFailureMessage(fallback)
}

function isTechnicalFailureMessage(value) {
  const token = unavailableToken(value)
  return isGenericFailureMessage(value) ||
    token.includes('request failed') ||
    token.includes('status code') ||
    token.includes('timeout') ||
    token.includes('http://') ||
    token.includes('https://') ||
    token.includes('<html') ||
    token.includes('<!doctype') ||
    token.includes('</') ||
    token.includes('stack trace') ||
    token.includes(' at ') ||
    /\bat\s+[\w.$<>]+\s*\([^)]*:\d+:\d+\)/.test(safeString(value)) ||
    token.includes('org.springframework') ||
    token.includes('com.valoran')
}

function hasAnyActiveFlag(layers, keys) {
  return layers.some(source => keys.some(key => isActiveUnavailableFlag(source[key])))
}

function hasAnyMarkerToken(layers, predicate) {
  const markerKeys = [
    'status',
    'state',
    'result',
    'code',
    'errorCode',
    'success',
    'marker',
    'type',
    'fallbackSource',
    'fallback_source',
    'msg',
    'message',
    'errorMsg',
    'errorMessage',
    'reason',
    'desc',
    'description'
  ]
  return layers.some(source => markerKeys.some(key => predicate(source[key])))
}

function isGenericFailureMessage(message, fallback = '') {
  const token = unavailableToken(message)
  const fallbackToken = unavailableToken(fallback)
  return Boolean(token) && (
    token === fallbackToken ||
    [
      'network error',
      'networkerror',
      'network request failed',
      'request failed',
      'requestfailed',
      'error',
      'failed',
      'failure',
      'fail'
    ].includes(token)
  )
}

function isOptimisticSuccessMessage(value) {
  const token = unavailableToken(value)
  if (!token) return false
  const hasSuccessCopy = /\b(?:ok|success|successful|done|saved|complete|completed|sucesso)\b/.test(token) || [
    'ok',
    'success',
    'successful',
    'done',
    'saved',
    'complete',
    'completed',
    'sucesso'
  ].includes(token) || token.includes('成功')
  const hasFailureContext = token.includes('fail') ||
    token.includes('error') ||
    isUnavailableToken(token) ||
    token.includes('blocked') ||
    token.includes('阻止') ||
    token.includes('失败') ||
    token.includes('失敗') ||
    token.includes('不可用') ||
    token.includes('未接入') ||
    token.includes('fake') ||
    token.includes('只读') ||
    token.includes('只讀')
  return hasSuccessCopy && !hasFailureContext
}

function unavailableToken(value) {
  return safeString(value).toLowerCase()
}

function isUnavailableToken(value) {
  const token = unavailableToken(value)
  return Boolean(token) && (
    [
      'stub',
      'mock',
      'mockmode',
      'mock_mode',
      'mock-mode',
      'mock mode',
      'mock_fallback',
      'mock-fallback',
      'mock fallback',
      'fallback',
      'fallback_used',
      'fallback-used',
      'fallback used',
      'dataunavailable',
      'data_unavailable',
      'data-unavailable',
      'data unavailable',
      'fail',
      'failed',
      'failure',
      'error',
      'rejected',
      'unavailable',
      'disabled',
      'read_only',
      'read_only_mode',
      'read-only-mode',
      'read only mode',
      'read_only_query',
      'read-only-query',
      'read only query',
      'read_only_action',
      'read-only-action',
      'read only action',
      'readonly',
      'readonlymode',
      'readonlyquery',
      'readonlyaction',
      'read only',
      'read-only',
      'unimplemented',
      'not_implemented',
      'not implemented',
      'not_supported',
      'not-supported',
      'not supported',
      'unsupported',
      'write_blocked',
      'writeblocked',
      'write blocked',
      'write-blocked',
      'backend_blocked',
      'backendblocked',
      'backend blocked',
      'backend-blocked',
      'blocked',
      'read_failure',
      'readfailure',
      'read-failure',
      'read failure',
      'read_unavailable',
      'readunavailable',
      'read-unavailable',
      'read unavailable',
      'route_unavailable',
      'routeunavailable',
      'route-unavailable',
      'route unavailable',
      'technical_error',
      'technicalerror',
      'technical-error',
      'technical error',
      'technical_marker',
      'technicalmarker',
      'technical-marker',
      'technical marker',
      'backend_failure',
      'backendfailure',
      'backend-failure',
      'backend failure',
      'technical_exception',
      'technicalexception',
      'technical-exception',
      'technical exception',
      'read_blocked',
      'readblocked',
      'read-blocked',
      'read blocked',
      'read_failed',
      'readfailed',
      'read-failed',
      'read failed',
      'need_login',
      '401',
      'unauthorized',
      '500'
    ].includes(token) ||
    token.includes('fallback') ||
    token.includes('dataunavailable') ||
    token.includes('data_unavailable') ||
    token.includes('data-unavailable') ||
    token.includes('data unavailable') ||
    token.includes('read_only') ||
    token.includes('read_only_mode') ||
    token.includes('read-only-mode') ||
    token.includes('read only mode') ||
    token.includes('read_only_query') ||
    token.includes('read only query') ||
    token.includes('read-only-query') ||
    token.includes('read_only_action') ||
    token.includes('read only action') ||
    token.includes('read-only-action') ||
    token.includes('readonlyquery') ||
    token.includes('readonlyaction') ||
    token.includes('readonlymode') ||
    token.includes('mock') ||
    token.includes('read only') ||
    token.includes('read-only') ||
    token.includes('unimplemented') ||
    token.includes('not_implemented') ||
    token.includes('not implemented') ||
    token.includes('not_supported') ||
    token.includes('not-supported') ||
    token.includes('not supported') ||
    token.includes('unsupported') ||
    token.includes('write_blocked') ||
    token.includes('writeblocked') ||
    token.includes('write blocked') ||
    token.includes('write-blocked') ||
    token.includes('backend_blocked') ||
    token.includes('backendblocked') ||
    token.includes('backend blocked') ||
    token.includes('backend-blocked') ||
    token.includes('read_failure') ||
    token.includes('readfailure') ||
    token.includes('read-failure') ||
    token.includes('read failure') ||
    token.includes('read_unavailable') ||
    token.includes('readunavailable') ||
    token.includes('read-unavailable') ||
    token.includes('read unavailable') ||
    token.includes('route_unavailable') ||
    token.includes('routeunavailable') ||
    token.includes('route-unavailable') ||
    token.includes('route unavailable') ||
    token.includes('technical_error') ||
    token.includes('technicalerror') ||
    token.includes('technical-error') ||
    token.includes('technical error') ||
    token.includes('technical_marker') ||
    token.includes('technicalmarker') ||
    token.includes('technical-marker') ||
    token.includes('technical marker') ||
    token.includes('backend_failure') ||
    token.includes('backendfailure') ||
    token.includes('backend-failure') ||
    token.includes('backend failure') ||
    token.includes('technical_exception') ||
    token.includes('technicalexception') ||
    token.includes('technical-exception') ||
    token.includes('technical exception') ||
    token.includes('read_blocked') ||
    token.includes('readblocked') ||
    token.includes('read-blocked') ||
    token.includes('read blocked') ||
    token.includes('read_failed') ||
    token.includes('readfailed') ||
    token.includes('read-failed') ||
    token.includes('read failed') ||
    token.includes('need_login') ||
    token.includes('unauthorized')
  )
}

function isActiveUnavailableFlag(value) {
  if (value === true || value === 1) return true
  const token = unavailableToken(value)
  return ['true', '1', 'yes'].includes(token) || isUnavailableToken(value)
}

export function hasUnavailableMarker(res) {
  const layers = responseLayers(res)
  const directFlagKeys = [
    'readOnly',
    'read_only',
    'readOnlyQuery',
    'read_only_query',
    'readOnlyMode',
    'read_only_mode',
    'readOnlyAction',
    'read_only_action',
    'readonlyAction',
    'readonly',
    'mock',
    'mockMode',
    'mock_mode',
    'mockFallback',
    'mock_fallback',
    'fallback',
    'fallbackUsed',
    'fallback_used',
    'dataUnavailable',
    'data_unavailable',
    'unimplemented',
    'notImplemented',
    'not_implemented',
    'notSupported',
    'not_supported',
    'unsupported',
    'stub',
    'writeBlocked',
    'write_blocked',
    'backendBlocked',
    'backend_blocked',
    'blocked',
    'readFailure',
    'read_failure',
    'readUnavailable',
    'read_unavailable',
    'routeUnavailable',
    'route_unavailable',
    'technicalError',
    'technical_error',
    'technicalException',
    'technical_exception',
    'technicalMarker',
    'technical_marker',
    'backendFailure',
    'backend_failure',
    'readBlocked',
    'read_blocked',
    'readFailed',
    'read_failed'
  ]
  if (layers.some(source => directFlagKeys.some(key => isActiveUnavailableFlag(source[key])))) return true
  if (layers.some(source => source.success === false || unavailableToken(source.success) === 'false')) return true
  const markerKeys = [
    'status',
    'state',
    'result',
    'code',
    'errorCode',
    'success',
    'marker',
    'type',
    'msg',
    'message',
    'errorMsg',
    'errorMessage',
    'fallbackSource',
    'fallback_source',
    'reason',
    'desc',
    'description'
  ]
  return layers.some(source => markerKeys.some(key => isUnavailableToken(source[key])))
}

export function accessTokenOf(res) {
  if (!isOkResponse(res)) return ''
  return firstStringFrom([
    contentOf(res),
    res && res.content,
    res && res.data,
    res
  ], ['accessToken', 'token', 'access_token'])
}

function hasSuccessMarker(res) {
  const markerKeys = ['status', 'state', 'result', 'code', 'success']
  return responseLayers(res).some(source => markerKeys.some(key => isSuccessToken(source[key])))
}

function isSuccessToken(value) {
  if (value === true) return true
  if (value === 0) return true
  const token = unavailableToken(value)
  return token === 'ok' || token === 'success' || token === 'true' || token === '0'
}

function firstStringFrom(sources, keys) {
  for (const source of sources) {
    const value = firstString(source, keys)
    if (value) return value
  }
  return ''
}

export function firstString(source, keys) {
  const data = source && typeof source === 'object' ? source : {}
  for (const key of keys) {
    const value = safeString(data[key])
    if (value) return value
  }
  return ''
}

export function normalizePlatform(item) {
  const source = item && typeof item === 'object' ? item : {}
  return {
    iconUrl: firstString(source, ['iconUrl', 'icon', 'flagUrl']),
    areaCode: firstString(source, ['areaCode', 'code']),
    countryName: firstString(source, ['countryName', 'name']),
    country: firstString(source, ['country', 'countryCode']),
    platform: firstString(source, ['platform']),
    currencyUnit: firstString(source, ['currencyUnit', 'currencySymbol']),
    currency: firstString(source, ['currency']),
    defaultLanguage: firstString(source, ['defaultLanguage', 'language'])
  }
}

export function normalizePlatformList(res) {
  const content = contentOf(res)
  const list = Array.isArray(res)
    ? res
    : Array.isArray(content.list)
      ? content.list
      : Array.isArray(content.platformList)
        ? content.platformList
        : Array.isArray(content.data)
          ? content.data
          : []

  return list
    .map(normalizePlatform)
    .filter((item) => item.areaCode || item.country || item.platform)
}

export function safeRedirectUrl(value) {
  const url = safeString(value)
  if (!url) return ''
  const browserWindow = typeof window === 'undefined' ? undefined : window
  const origin = browserWindow && browserWindow.location && browserWindow.location.origin
  try {
    const parsed = origin ? new URL(url, origin) : new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : ''
  } catch (e) {
    return ''
  }
}

export function googleRedirectUrl(res) {
  const content = contentOf(res)
  return safeRedirectUrl(firstString(content, ['authUrl', 'url', 'redirectUri']))
}

export function safeTrackCompleteRegistration(vm, res) {
  if (!isOkResponse(res) || isNeedLogin(res)) return false
  const content = contentOf(res)
  if (!content.isReport) return false

  const pixelId = firstString(content, ['fbPixId', 'pixelId', 'fbid'])
  if (!pixelId) return false

  const target = safeString(content.target).toLowerCase()
  const win = browserWindow()
  try {
    const browserLocalStorage = safeLocalStorage()
    if (target === 'ks') {
      const kwaiq = win && win.kwaiq
      if (!kwaiq || typeof kwaiq.load !== 'function' || typeof kwaiq.instance !== 'function') {
        return false
      }
      kwaiq.load(pixelId)
      const tracker = kwaiq.instance(pixelId)
      if (tracker && typeof tracker.track === 'function') {
        tracker.track('completeRegistration')
        return true
      }
      return false
    }

    const pixel = vm && vm.$pixel
    if (pixel && typeof pixel.setFbId === 'function' && typeof pixel.callTrack === 'function') {
      pixel.setFbId(pixelId)
      pixel.callTrack('track', 'CompleteRegistration')
      if (typeof pixel.sendEventToAndroid === 'function') {
        pixel.sendEventToAndroid(JSON.stringify({
          data: [{
            event_name: 'CompleteRegistration',
            status: 'success',
            value: 0,
            currency: safeString(content.currency || safeGetStorageItem(browserLocalStorage, 'currency')) || (SINGLE_COUNTRY && SINGLE_COUNTRY.currency) || 'XOF'
          }]
        }))
      }
      return true
    }

    if (win && typeof win.fbq === 'function') {
      win.fbq('track', 'CompleteRegistration')
      return true
    }
  } catch (e) {
    return false
  }

  return false
}
